/* eslint-disable max-lines-per-function */
import { useMutation, useQuery } from "@apollo/client";
import { Formik } from "formik";
import { get, includes, keyBy, orderBy } from "lodash";
import React from "react";
import ReactQuill from "react-quill";
import { RouteComponentProps } from "@reach/router";
import {
  ChangeWaitlistUpdateSettings,
  ChangeWaitlistUpdateSettingsVariables,
  FetchWaitlistUpdateSettings,
  FetchWaitlistUpdateSettingsVariables,
  ToggleWaitlistUpdate,
  ToggleWaitlistUpdateVariables,
} from "types/gql-types";
import { authorizeForRole } from "components/auth/authorize-for-role";
import { Roles, Viewer } from "components/auth/types";
import { MaterialFormikSelect as Select } from "components/haven-design/form/select";
import { VIEWER_QUERY } from "components/auth/query";
import { apolloClient } from "data/graphql";
import styles from "./index.module.css";
import {
  CHANGE_WAITLIST_UPDATE_SETTINGS,
  TOGGLE_WAITLIST_UPDATE_FOR_LIST,
  WAITLIST_UPDATE_SETTINGS,
} from "./query";

type MessageType = "SMS" | "LETTER" | "EMAIL";

enum LetterMode {
  All = "all",
  MailingOnly = "mailingOnly",
  None = "none",
}

type Settings = FetchWaitlistUpdateSettings["organization"]["scheduledUpdateRequests"]["settings"];

const isNotAuthorized = (viewer: Viewer): boolean => !includes(viewer.roles, Roles.Supervisor);

const getTemplateDelta = (data: FetchWaitlistUpdateSettings | undefined, type: MessageType) => {
  const templates = get(data, ["organization", "scheduledUpdateRequests", "templates"], []);
  const keyed = keyBy(templates, (t) => t.type);
  const delta = get(keyed, [type, "body"], false);
  if (!delta) return "";

  try {
    return JSON.parse(delta);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log("getTemplateDelta failed for type", type, e);
    return "";
  }
};

const PreviewComponent: React.FC<{data: any}> = ({ data }) => {
  const [messageType, setMessageType] = React.useState<MessageType>("SMS");

  return <div className={styles.preview}>
    <h3>Message preview</h3>
    <div className={styles.previewTypes}>
      {["Letter", "Email", "SMS"].map((type) => (
        <a
          href=""
          key={type}
          onClick={(event) => {
            event.preventDefault();
            setMessageType(type.toUpperCase() as MessageType);
          }}
        >
          {type}
        </a>
      ))}
    </div>
    <div>
      <ReactQuill
        readOnly
        modules={{
          toolbar: false,
        }}
        value={getTemplateDelta(data, messageType)}
      />
    </div>
  </div>;
};

const AutomaticUpdatePropertySelectionForm: React.FC<any> =
({ viewer, refetch, interestLists }) => {
  const [toggleUpdate] = useMutation<ToggleWaitlistUpdate, ToggleWaitlistUpdateVariables>(
    TOGGLE_WAITLIST_UPDATE_FOR_LIST,
  );
  return <div>
    <p>Enable the properties that you want automatic list updates for.</p>
    {orderBy(interestLists, ["name"], ["asc"])
      .filter(({ type }) => type === "property" || type === "interest")
      .filter(({ id }) => viewer.lists.some((list:any) => list.id === id))
      .map(({ id, name, scheduledUpdate: { enabled } }) => (
        <div key={id}>
          <label className={styles.label}>
            <input
              type="checkbox"
              checked={enabled || false}
              disabled={isNotAuthorized(viewer)}
              onChange={async () => {
                await toggleUpdate({ variables: { id, enabled: !enabled } });
                await apolloClient.refetchQueries({
                  include: [VIEWER_QUERY],
                });
                refetch();
              }}
            />
            {name}
          </label>
        </div>
      ))}
  </div>;
};

const UpdateSettingsFormComponent: React.FC<any> = ({ viewer, refetch, settings }) => {

  const [changeSettings] = useMutation<
  ChangeWaitlistUpdateSettings,
  ChangeWaitlistUpdateSettingsVariables
  >(CHANGE_WAITLIST_UPDATE_SETTINGS);
  return <Formik<Settings>
    initialValues={settings}
    onSubmit={async (formValues: any, form: any) => {
      await changeSettings({
        variables: {
          id: viewer.organizationId,
          options: {
            letterMode: formValues.letterMode,
          },
        },
      });

      refetch();
      form.setSubmitting(false);
    }}
  >
    {(form: any) => (
      <>
        <Select
          name="letterMode"
          title="Letter mode"
          value={form.values.letterMode}
          disabled={isNotAuthorized(viewer)}
          onChange={async (event) => {
            await form.handleChange(event);
            form.submitForm();
          }}
          options={[
            {
              label: "Send letters to all applicants",
              value: LetterMode.All,
            },
            {
              label: "Only send letters to applicants with only a mailing address",
              value: LetterMode.MailingOnly,
            },
            {
              label: "Do not send any letters",
              value: LetterMode.None,
            },
          ]}
        />

        {form.values.letterMode === LetterMode.None && (
          <div>
            <strong>Note: </strong> Not all applicants will be contacted with this option.
          You will be responsible for calling each applicant without an email or cell
          phone individually.
            <br />
            <br />
          </div>
        )}
      </>
    )}
  </Formik>;
};

const ScheduledUpdates: React.FC<RouteComponentProps<{ path: string; viewer: Viewer }>> =
(props) => {
  if (!props.viewer) return null;
  if (!props.viewer.organizationId) return null;

  const vars = { variables: { id: props.viewer.organizationId } };
  const { loading, error, data, refetch } = useQuery<
  FetchWaitlistUpdateSettings,
  FetchWaitlistUpdateSettingsVariables
  >(WAITLIST_UPDATE_SETTINGS, vars);

  if (loading || error || !data) return null;

  const {
    organization: {
      interestLists,
      scheduledUpdateRequests: { settings },
    },
  } = data;

  return (
    <div className={styles.content}>
      <div className={styles.settings}>
        <h2 className="margin-bottom-l">List Update</h2>
        <div className={styles.description}>
          <p>
            With list updates enabled, Haven Connect will automatically send letters, texts, and
            emails to all active applicants on the checked waitlists below once a year (usually
            in the spring), directing them to update their income, household size, and contact
            information.
          </p>
        </div>

        <UpdateSettingsFormComponent viewer={props.viewer} refetch={refetch} settings={settings} />

        <AutomaticUpdatePropertySelectionForm
          viewer={props.viewer} refetch={refetch} interestLists={interestLists} />
      </div>

      <PreviewComponent data={data}/>
    </div>
  );
};

export default authorizeForRole(
  [Roles.Admin, Roles.PropertyManager, Roles.Supervisor],
  ScheduledUpdates,
);
